import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Phone, Mail, Linkedin, Facebook, Twitter, Instagram, Youtube, ArrowUp } from "lucide-react";
import { Link } from "react-router-dom";
import { useCookieContext } from "@/contexts/CookieContext";
import { ContactModal } from "@/components/ContactModal";
import { useTranslation } from "@/hooks/useTranslation";

const Footer = () => {
  const { t } = useTranslation();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const { openSettings } = useCookieContext();
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };
  return (
    <footer className="bg-gradient-to-br from-neutral-50 to-white rounded-none">
      <div className="container-padding py-12 rounded-t-3xl backdrop-blur-sm bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2 space-y-3">
                <Link to="/about/vidavo" aria-label={t.footer.aboutVidavo}>
                  <img
                    src="/lovable-uploads/fc26f480-9c94-4be3-b251-a1f837add861.png"
                    alt="Vidavo Logo"
                    className="h-12 w-auto hover:opacity-80 transition-opacity cursor-pointer"
                  />
                </Link>
                <div></div>
              </div>
            </div>

            <p className="text-sm text-neutral-700 mb-4 leading-relaxed">{t.footer.partnerText}</p>

            <Button asChild className="w-full">
              <a href="mailto:info@vidavo.eu?subject=Interest%20to%20join%20the%20Vida24%20ecosystem%20as%20partner">
                {t.footer.becomePartner}
              </a>
            </Button>

            {/* Certification Logos */}
          </div>

          {/* Our Offices */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-secondary" />
              <h2 className="font-semibold text-primary">{t.footer.ourOffices}</h2>
            </div>
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-neutral-800">{t.footer.thessalonikiHQ}</p>
                <a
                  href="https://www.google.com/maps/place/VIDAVO/@40.5635331,22.9943747,17z/data=!3m1!4b1!4m6!3m5!1s0x14a83f66d94f6389:0x1874e1c6d032e216!8m2!3d40.5635331!4d22.9943747!16s%2Fg%2F12lw05_b6?entry=ttu&g_ep=EgoyMDI1MTExMi4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm mt-1 text-neutral-700 hover:text-primary transition-colors cursor-pointer inline-block underline hover:no-underline"
                >
                  {t.footer.thessalonikiAddress}
                  <br />
                  {t.footer.thessalonikiAddress2}
                </a>
              </div>
              <div>
                <p className="font-semibold text-neutral-800">{t.footer.athens}</p>
                <a
                  href="https://www.google.com/maps/place/Πριήνης+4,+Νέα+Σμύρνη+171+22/@37.9446956,23.699538,17z/data=!3m1!4b1!4m6!3m5!1s0x14a1bc45dd1d0f0f:0x67a6117f8fc0ab3a!8m2!3d37.9446914!4d23.7021183!16s%2Fg%2F11kmcq3_bc?entry=ttu&g_ep=EgoyMDI1MTExMi4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm mt-1 text-neutral-700 hover:text-primary transition-colors cursor-pointer inline-block underline hover:no-underline"
                >
                  {t.footer.athensAddress}
                </a>
              </div>
              <div>
                <p className="font-semibold text-neutral-800">{t.footer.ioannina}</p>
                <a
                  href="https://www.google.com/maps/place/Επιστημονικό+%26+Τεχνολογικό+Πάρκο+Ηπείρου/@39.6221478,20.8465979,17z/data=!3m1!4b1!4m6!3m5!1s0x135beec84eac1a6b:0xcc2a4a74fe708440!8m2!3d39.6221478!4d20.8465979!16s%2Fg%2F11h0g47kc?entry=ttu&g_ep=EgoyMDI1MTExMi4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm mt-1 text-neutral-700 hover:text-primary transition-colors cursor-pointer inline-block underline hover:no-underline"
                >
                  {t.footer.ioanninaAddress}
                </a>
              </div>
              <div>
                <p className="font-semibold text-neutral-800">{t.footer.vidavoEU}</p>
                <a
                  href="https://www.google.com/maps/place/Quai+Sur+-+Meuse+19,+4000+Li%C3%A8ge,+%CE%92%CE%AD%CE%BB%CE%B3%CE%B9%CE%BF/@50.6418904,5.5762433,17z/data=!3m1!4b1!4m6!3m5!1s0x47c0fa0f2f493019:0xc2d3e0ef8f40cd09!8m2!3d50.6418904!4d5.5762433!16s%2Fg%2F11nntqcbhr?entry=ttu&g_ep=EgoyMDI1MTExNi4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm mt-1 text-neutral-700 hover:text-primary transition-colors cursor-pointer inline-block underline hover:no-underline"
                >
                  {t.footer.vidavoEUAddress}
                </a>
              </div>
            </div>
          </div>

          {/* Call us */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Phone className="w-5 h-5 text-secondary" />
              <h2 className="font-semibold text-primary">{t.footer.callUsAt}</h2>
            </div>
            <a
              href="tel:+302310474762"
              className="font-semibold text-neutral-800 hover:text-primary transition-colors cursor-pointer inline-block underline hover:no-underline"
            >
              +30 2310474762
            </a>
          </div>

          {/* Email us */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Mail className="w-5 h-5 text-secondary" />
              <h2 className="font-semibold text-primary">{t.footer.emailUsAt}</h2>
            </div>
            <a
              href="mailto:vidavo@vidavo.eu"
              className="font-semibold text-neutral-800 hover:text-primary transition-colors cursor-pointer inline-block underline hover:no-underline"
            >
              vidavo@vidavo.eu
            </a>
          </div>

          {/* Follow us */}
          <div>
            <h2 className="font-semibold text-primary mb-4">{t.footer.followUs}</h2>
            <div className="flex gap-3 mb-4">
              <a
                href="https://www.linkedin.com/company/vidavo-s.a./"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-secondary rounded-lg flex items-center justify-center text-white hover:bg-secondary/80 transition-all hover:scale-110 shadow-sm"
              >
                <img
                  src="/lovable-uploads/2d0f8ec1-6fd1-48fc-a567-cf6d12219cc5.png"
                  alt="LinkedIn"
                  className="w-4"
                />
              </a>
              <a
                href="https://www.facebook.com/VIDAVO/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-accent-teal rounded-lg flex items-center justify-center text-white hover:bg-accent-teal/80 transition-all hover:scale-110 shadow-sm"
              >
                <img
                  src="/lovable-uploads/3a4e9679-0849-4d7e-9a4a-1e0de9fe5cc3.png"
                  alt="Facebook"
                  className="w-4 h-4"
                />
              </a>

              <a
                href="https://www.instagram.com/vidavo.mhealth/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-gradient-to-r from-secondary to-accent-teal rounded-lg flex items-center justify-center text-white hover:opacity-80 transition-all hover:scale-110 shadow-sm"
              >
                <img
                  src="/lovable-uploads/a1286d9a-f246-4fbf-81f4-bf0ebe5a453b.png"
                  alt="Instagram"
                  className="w-4"
                />
              </a>
              <a
                href="https://www.youtube.com/user/vidavo1"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 bg-accent-teal rounded-lg flex items-center justify-center text-white hover:bg-accent-teal/80 transition-all hover:scale-110 shadow-sm"
              >
                <img
                  src="/lovable-uploads/325eb550-8ffd-4be8-a213-a4b03bdde609.png"
                  alt="YouTube"
                  className="w-4 h-4"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-neutral-200 pt-6">
          {/* Certification Logos */}
          <div className="flex justify-center mb-6">
            <img
              src="/lovable-uploads/d061259f-66a7-4953-abc4-0360fd8e9a09.png"
              alt="Certification Logos - ISO 9001, ISO 13485, ISO 37001, ISO/IEC 27001, ISO 27701"
              className="h-10 w-auto opacity-80 hover:opacity-100 transition-opacity"
            />
          </div>

          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <p className="text-sm text-gray-600 text-center">
              {t.footer.copyright} {t.footer.allRightsReserved} |
              <Link to="/personal-data" className="hover:text-primary transition-colors ml-1 underline">
                {t.footer.personalData}
              </Link>{" "}
              |
              <button onClick={openSettings} className="hover:text-primary transition-colors ml-1 underline">
                {t.footer.cookieNotice}
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Back to top button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-secondary to-accent-teal hover:from-secondary/90 hover:to-accent-teal/90 text-white rounded-full flex items-center justify-center shadow-lg transition-all hover:shadow-xl hover:scale-110 z-50"
        aria-label={t.footer.backToTop}
      >
        <ArrowUp className="w-5 h-5" />
      </button>

      <ContactModal open={isContactModalOpen} onOpenChange={setIsContactModalOpen} />
    </footer>
  );
};
export default Footer;
